import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { PmaService } from '../../../pma/pma.service';
import { map, startWith } from 'rxjs/operators';
import { Insurable } from '../../../core/models/insurable.model';
import { BrandingService } from '../../../core/services/branding.service';

interface BuildingDisplay {
  insurable: Insurable;
  displayText: string;
}

@Component({
    selector: 'ui-building',
    templateUrl: './ui-building.component.html',
    standalone: false
})
export class UiBuildingComponent implements OnChanges {
  @Input() buildingControl: FormControl;
  @Input() buildings;
  @Input() unitControl;
  @Input() loader = false;
  filteredInsurables$: Observable<BuildingDisplay[]>;
  insurables;
  communitiesTypeIds;
  useBuildingAddressInSelector: boolean = false;

  constructor(private brandingService: BrandingService) {
    this.communitiesTypeIds = PmaService.getTypeIdsByName('buildings');
    this.checkBrandingAttribute();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['buildings'] && changes['buildings']?.currentValue) {
      this.setFilteredInsurables();
    }
  }

  checkBrandingAttribute(): void {
    const attribute = this.brandingService.getBrandingInfoSnapshot()?.profile_attributes
      .find(item => item.name === 'use_building_address_in_selector');
    this.useBuildingAddressInSelector = attribute?.value === 'true' || attribute?.value === true;
  }

  getBuildingDisplayText(insurable: Insurable): string {
    if (!insurable) return '';

    const buildingName = insurable.title || '';
    const addressLine1 = this.getAddressLine1(insurable);

    // If Building Name IS equal to Building Address Line 1, show just the address line 1
    // (ignore use_building_address_in_selector flag completely in this case)
    if (buildingName.trim() === addressLine1.trim()) {
      return addressLine1;
    }

    // If use_building_address_in_selector = true, show just the address line 1
    if (this.useBuildingAddressInSelector) {
      return addressLine1;
    }

    // If use_building_address_in_selector = false or not present,
    // show "Building - {Name}, {Address Line 1}"
    return `Building - ${buildingName}, ${addressLine1}`;
  }

  setFilteredInsurables() {
    this.filteredInsurables$ = this.buildingControl.valueChanges
      .pipe(
        startWith(''),
        map(value => {
          if (value && typeof value === 'object' && 'title' in value) {
            return this.getBuildingDisplayText(value);
          }
          return value || '';
        }),
        map(searchTerm => {
          const filtered = searchTerm
            ? this.filterInsurables(this.buildings, searchTerm)
            : this.buildings.slice(0);

          return filtered.map(insurable => ({
            insurable,
            displayText: this.getBuildingDisplayText(insurable)
          }));
        })
      );
  }

  private filterInsurables(insurables: Insurable[], searchTerm: string): Insurable[] {
    const filterValue = searchTerm.toLowerCase();

    return insurables.filter(insurable => {
      const displayText = this.getBuildingDisplayText(insurable);
      return displayText && displayText.toLowerCase().includes(filterValue);
    });
  }

  /**
   * Gets Address Line 1 (street_number + street_name) for building display
   */
  getAddressLine1(insurable: Insurable): string {
    if (!insurable) return '';

    if (insurable.primary_address) {
      const addr = insurable.primary_address;
      const parts = [
        addr.street_number,
        addr.street_name
      ].filter(part => part && part.trim() !== '');

      return parts.join(' ') || insurable.title;
    }

    return insurable.title;
  }

  get insurableField() {
    return this.buildingControl;
  }

  displayFnInsurable = (insurable?: Insurable): string | undefined => {
    if (!insurable) return undefined;
    return this.getBuildingDisplayText(insurable);
  }

  reset() {
    this.insurableField.reset(null);
    this.unitControl.reset(null);
  }
}
